import React, { useState, useEffect, useCallback } from "react";
import Card from "../../../components/Card";
import TextInput from "../../../components/TextInput";
import cn from "classnames";
import styles from "./CenterDetailsForm.module.sass";
import Icon from "../../../components/Icon";
import { getApiKeys, saveApiKey, checkApiKey } from "../../../services/apikey.service";
import { getOpcos } from "../../../services/opco.service";
import { showError, showSuccess } from "../../../services/toast.service";

// Cette configuration sera remplacée par les données dynamiques depuis PocketBase
let OPCO_CONFIG = [];

// Helper to determine API key status based on expiration date from OPCO API
const getApiKeyStatus = (expirationDateString, lastUpdatedString = null) => {
  // Si pas de date d'expiration, utiliser l'ancienne logique avec la date de mise à jour
  if (!expirationDateString) {
    if (!lastUpdatedString) {
      return { text: "Jamais mise à jour", className: "status-gray-dark" };
    }
    
    // Ancienne logique de fallback (6 mois après la dernière mise à jour)
    const updatedDate = new Date(lastUpdatedString);
    const now = new Date();
    const sixMonths = 6 * 30 * 24 * 60 * 60 * 1000;
    const expirationDate = new Date(updatedDate.getTime() + sixMonths);
    const timeLeft = expirationDate.getTime() - now.getTime();
    
    if (timeLeft <= 0) {
      return { text: "Expirée (estimée)", className: "status-red-dark" };
    } else if (timeLeft < 30 * 24 * 60 * 60 * 1000) {
      return { text: "Expire bientôt (estimée)", className: "status-yellow" };
    } else {
      return { text: "Active (estimée)", className: "status-green-dark" };
    }
  }

  // Nouvelle logique avec la vraie date d'expiration de l'OPCO
  const expirationDate = new Date(expirationDateString);
  const now = new Date();
  const timeLeft = expirationDate.getTime() - now.getTime();
  const sevenDays = 7 * 24 * 60 * 60 * 1000; // 7 jours en millisecondes

  if (timeLeft <= 0) {
    // Date d'expiration passée
    return { text: "Expirée", className: "status-red-dark" };
  } else if (timeLeft <= sevenDays) {
    // Expire dans les 7 prochains jours
    const daysLeft = Math.ceil(timeLeft / (24 * 60 * 60 * 1000));
    return { 
      text: `Expire dans ${daysLeft} jour${daysLeft > 1 ? 's' : ''}`, 
      className: "status-yellow" 
    };
  } else {
    // Valide
    return { text: "Active", className: "status-green-dark" };
  }
};

// Helper to format expiration date for display
const formatExpirationDate = (expirationDateString) => {
  if (!expirationDateString) return null;
  
  const date = new Date(expirationDateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const ApiKey = ({ partner }) => {
  const [apiKeys, setApiKeys] = useState({});
  const [initialApiKeys, setInitialApiKeys] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [testStatus, setTestStatus] = useState({});
  const [lastUpdatedDates, setLastUpdatedDates] = useState({});
  const [expirationDates, setExpirationDates] = useState({});
  const [opcoList, setOpcoList] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        // Vérifier que le partenaire est disponible
        if (!partner?.id) {
          console.log('Partner non disponible, attente...');
          return;
        }

        // 1. Récupérer la liste des OPCO depuis PocketBase
        const opcos = await getOpcos();
        
        // 2. Construire la configuration dynamique
        const dynamicConfig = opcos.map(opco => ({
          id: opco.id,
          name: opco.name.toLowerCase().replace(/\s+/g, '_'), // Pour les clés d'état
          label: `Clé d'API ${opco.name}`,
          opcoId: opco.id,
          opcoName: opco.name
        }));

        // 3. Récupérer les clés API existantes avec expansion des relations
        const keys = await getApiKeys(partner.id);
        
        const keyMap = {};
        const updatedDatesMap = {};
        const expirationDatesMap = {};

        dynamicConfig.forEach(config => {
          const foundKey = keys.find(key => 
            key.expand?.opco_id?.id === config.opcoId && 
            key.partner_id === partner.id
          );
          keyMap[config.name] = foundKey ? foundKey.apikey : '';
          updatedDatesMap[config.name] = foundKey ? foundKey.updated : null;
          expirationDatesMap[config.name] = foundKey ? foundKey.date_fin_validite : null;
        });

        if (isMounted) {
          OPCO_CONFIG = dynamicConfig; // Mettre à jour la configuration globale
          setOpcoList(opcos);
          setApiKeys(keyMap);
          setInitialApiKeys(keyMap);
          setLastUpdatedDates(updatedDatesMap);
          setExpirationDates(expirationDatesMap);
        }
      } catch (error) {
        if (error.name !== 'AbortError' && !error.message?.includes('autocancelled')) {
          console.error("Error fetching data:", error);
          showError(`Erreur lors de la récupération des données: ${error.message || error}`);
        } else {
          console.warn("Data fetch was cancelled (likely due to Strict Mode or rapid re-render):", error);
        }
      }
    };

    if (partner?.id) {
      fetchData();
    }

    return () => {
      isMounted = false;
    };
  }, [partner?.id]);

  const hasUnsavedChanges = useCallback(() => {
    return Object.keys(apiKeys).some(key => apiKeys[key] !== initialApiKeys[key]);
  }, [apiKeys, initialApiKeys]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setApiKeys((prevKeys) => ({
      ...prevKeys,
      [name]: value,
    }));
  };

  const handleTestApiKey = async (opcoNameInState) => {
    const config = OPCO_CONFIG.find(c => c.name === opcoNameInState);
    if (!config) return;

    // Vérifier si une clé API est présente
    if (!apiKeys[opcoNameInState]) {
      showError(`Veuillez saisir une clé API pour ${config.label.replace('Clé d\'API ', '')}`);
      return;
    }

    setTestStatus(prev => ({ ...prev, [opcoNameInState]: 'testing' }));

    try {
      console.log(`Vérification de la clé API ${config.label}`);

      // Appel au service pour vérifier la clé API avec l'ID OPCO
      const result = await checkApiKey(
        partner.id, 
        config.opcoId, 
        apiKeys[opcoNameInState]
      );

      // Vérifier le résultat
      if (result.isValid) {
        // Afficher les informations détaillées du test
        const partnerName = result.partnerInfo?.name || 'Partenaire inconnu';
        const opcoTested = result.opcoTested || config.opcoName;
        const apiKeyTested = result.apiKeyTested || 'Clé masquée';
        
        console.log('Test réussi pour:');
        console.log('- Partenaire:', partnerName);
        console.log('- OPCO:', opcoTested);
        console.log('- Clé testée:', apiKeyTested);
        
        showSuccess(
          `Clé API ${config.label.replace('Clé d\'API ', '')} validée avec succès!\n` +
          `Partenaire: ${partnerName}\n` +
          `OPCO: ${opcoTested}\n` +
          `Clé: ${apiKeyTested}`
        );
        setTestStatus(prev => ({ ...prev, [opcoNameInState]: 'success' }));
      } else {
        showError(`La clé API ${config.label.replace('Clé d\'API ', '')} est invalide: ${result.message}`);
        setTestStatus(prev => ({ ...prev, [opcoNameInState]: 'error' }));
      }

    } catch (error) {
      console.error(`Erreur lors de la vérification de la clé API ${opcoNameInState}:`, error);
      showError(`Erreur lors du test de la clé API ${config.label.replace('Clé d\'API ', '')}: ${error.message || error}`);
      setTestStatus(prev => ({ ...prev, [opcoNameInState]: 'error' }));
    } finally {
      // Réinitialiser le statut de test après 3 secondes
      setTimeout(() => {
        setTestStatus(prev => ({ ...prev, [opcoNameInState]: null }));
      }, 3000);
    }
  };

  const handleFormSubmission = async () => {
    setIsSaving(true);

    try {
      const promises = [];

      for (const config of OPCO_CONFIG) {
        if (apiKeys[config.name] !== initialApiKeys[config.name]) {
          promises.push(
            saveApiKey(partner.id, config.opcoId, apiKeys[config.name])
              .then((record) => {
                setInitialApiKeys(prev => ({ ...prev, [config.name]: apiKeys[config.name] }));
                setLastUpdatedDates(prev => ({ ...prev, [config.name]: record.updated }));
                setExpirationDates(prev => ({ ...prev, [config.name]: record.date_fin_validite }));
                showSuccess(`Clé API ${config.label.replace('Clé d\'API ', '')} mise à jour avec succès !`);
              })
              .catch(error => {
                console.error(`Error saving API key ${config.opcoId}:`, error);
                showError(`Erreur lors de la sauvegarde de la clé API ${config.label.replace('Clé d\'API ', '')}: ${error.message || error}`);
              })
          );
        }
      }

      await Promise.all(promises);

      if (promises.length === 0 && hasUnsavedChanges()) {
        showError("Aucune modification détectée pour l'enregistrement.");
      } else if (promises.length === 0 && !hasUnsavedChanges()) {
        showSuccess("Aucune modification à enregistrer.");
      }

    } catch (error) {
      console.error("Global error during API key save:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setApiKeys(initialApiKeys);
    setTestStatus({});
  };

  const formatLastUpdate = (dateString) => {
    if (!dateString) return "Jamais mise à jour";
    try {
      const date = new Date(dateString);
      return `Dernière mise à jour: ${date.toLocaleString('fr-FR', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      }).replace(/\//g, '/')}`;
    } catch (e) {
      console.error("Error formatting date:", e);
      return "Date invalide";
    }
  };


  const getTestButtonContent = (keyName) => {
    const status = testStatus[keyName];
    if (status === 'testing') {
      return <Icon name="loading" className={styles.loadingIcon} />;
    } else if (status === 'success') {
      return <Icon name="check" className={styles.successIcon} />;
    } else if (status === 'error') {
      return <Icon name="close" className={styles.errorIcon} />;
    }
    return "Tester";
  };

  return (
    <Card className={cn(styles.card)} title="Configuration des Clés API" classTitle="title-green">
      <div className={styles.description}>
        <form onSubmit={(e) => e.preventDefault()}>
          {OPCO_CONFIG.map((config) => {
            const keyStatus = getApiKeyStatus(expirationDates[config.name], lastUpdatedDates[config.name]);
            return (
              <div key={config.name} className={styles.inputGroup}>
                {/* New layout for label and badge */}
                <div className={styles.labelAndBadgeRow}>
                  <div>{config.label}</div>
                  {/* Show badge if there's an expiration date or updated date */}
                  {(expirationDates[config.name] || lastUpdatedDates[config.name]) && (
                    <span className={cn('status-type', keyStatus.className, styles.apiKeyStatusBadge)}>
                      {keyStatus.text}
                    </span>
                  )}
                </div>
                <div className={styles.inputRow}>
                  <TextInput
                    name={config.name}
                    placeholder={`Saisir la clé API pour ${config.label.replace('Clé d\'API ', '')}`}
                    value={apiKeys[config.name] || ''}
                    onChange={handleChange}
                    className={styles.inputField}
                    // type="password"
                  />
                  <button
                    className={cn("button-stroke", styles.testButton, {
                      [styles.testButtonLoading]: testStatus[config.name] === 'testing',
                      [styles.testButtonSuccess]: testStatus[config.name] === 'success',
                      [styles.testButtonError]: testStatus[config.name] === 'error',
                    })}
                    onClick={() => handleTestApiKey(config.name)}
                    disabled={testStatus[config.name] === 'testing'}
                  >
                    {getTestButtonContent(config.name)}
                  </button>
                </div>
                <div className={styles.lastUpdateText}>
                  {expirationDates[config.name] ? (
                    `Expire le: ${formatExpirationDate(expirationDates[config.name])}`
                  ) : (
                    formatLastUpdate(lastUpdatedDates[config.name])
                  )}
                </div>
              </div>
            );
          })}

          <div className={styles.buttons}>
            <button
              className={cn("button-stroke", styles.button)}
              onClick={handleReset}
              disabled={isSaving || !hasUnsavedChanges()}
            >
              Annuler
            </button>
            <button
              className={cn("button", styles.button)}
              onClick={handleFormSubmission}
              disabled={isSaving || !hasUnsavedChanges()}
            >
              {isSaving ? "Enregistrement..." : "Enregistrer les modifications"}
            </button>
          </div>
        </form>
      </div>
    </Card>
  );
};

export default ApiKey;